import URI from 'urijs'
import moment from 'moment'
import config from '@/config'
import { getDaysInMonth, isCurrentMonth, addHours, addDays } from './dateutil'
import { regression, round2 } from './math'

export function redirect(url, qs) {
  document.location.href = URI(url)
    .query(qs || {})
    .toString()
}

export function objectClone(source) {
  return JSON.parse(JSON.stringify(source))
}

function getMonthlyPeriod(from, to, period) {
  const months = []
  const preMonth = moment(to).subtract(period - 1, 'month')
  const endMonth = moment(to)
  while (preMonth.isSameOrAfter(from)) {
    months.push(`${preMonth.format('YYYY-MM')}~${endMonth.format('YYYY-MM')}`)
    endMonth.subtract(1, 'month')
    preMonth.subtract(1, 'month')
  }
  return months
}

function getMonths(from, to) {
  const months = []
  const endMonth = moment(to)
  while (endMonth.isSameOrAfter(from)) {
    months.push(endMonth.format('YYYY-MM'))
    endMonth.subtract(1, 'month')
  }
  return months
}

export function getDate(type) {
  const to = moment().startOf('month').format('YYYY-MM')
  const from = moment(config.serviceStartDate).format('YYYY-MM')

  const periods = {
    threeMonths: 3,
    sixMonths: 6,
    oneYear: 12,
  }

  return type === 'oneMonth'
    ? getMonths(from, to)
    : getMonthlyPeriod(from, to, periods[type])
}

export function isObjEqual(a, b) {
  return a && b && Object.keys(a).every((k) => a[k] === b[k])
}

export function distinct(list, ...params) {
  if (!list) {
    return []
  }

  const keys = []
  const localParams =
    params.length === 0 && list.length > 0 ? Object.keys(list[0]) : params

  list.forEach((e) => {
    if (localParams.length === 1) {
      if (e[localParams[0]] !== undefined) {
        if (keys.indexOf(e[localParams[0]]) === -1) {
          keys.push(e[localParams[0]])
        }
      }
    } else {
      const key = localParams.reduce((obj, param) => {
        obj[param] = e[param]
        return obj
      }, {})
      if (!keys.find((k) => isObjEqual(k, key))) {
        keys.push(key)
      }
    }
  })

  return keys
}

export function objectAdd(target, source, toTarget = false) {
  const keys = [...new Set([...Object.keys(target), ...Object.keys(source)])]

  return keys.reduce(
    (obj, name) => {
      if (
        typeof target[name] === 'number' ||
        typeof source[name] === 'number'
      ) {
        obj[name] = (source[name] || 0) + (target[name] || 0)
      } else {
        obj[name] = source[name] || target[name]
      }
      return obj
    },
    toTarget ? target : {}
  )
}
export function hasProperty(target, property) {
  return Object.prototype.hasOwnProperty.call(target, property)
}
export function hasNestedProperties(target, properties) {
  if (properties.length === 0) return true
  if (!hasProperty(target, properties[0])) return false

  return hasNestedProperties(
    target[properties[0]],
    properties.slice(1, properties.length)
  )
}

function exchangeCost(item, displayCurrency, getExchangeRate, context) {
  const isNotUSD = (name) => !name.endsWith('_in_usd') && name !== 'currency'
  const result = { costs: {} }

  if (displayCurrency === item.currency) {
    result.costs = Object.keys(item).reduce((obj, name) => {
      if (isNotUSD(name)) obj[name] = item[name]
      return obj
    }, {})
  } else if (config.platform.isGCP && displayCurrency === 'USD') {
    result.costs = Object.keys(item).reduce((obj, name) => {
      if (isNotUSD(name)) obj[name] = item[`${name}_in_usd`]
      return obj
    }, {})
  } else {
    const rate = getExchangeRate(item.currency, context) || 1
    if (displayCurrency === 'KRW') {
      result.costs = Object.keys(item).reduce((obj, name) => {
        if (isNotUSD(name)) obj[name] = Math.floor(round2(item[name]) * rate)
        return obj
      }, {})
    } else {
      const targetRate = getExchangeRate(displayCurrency, context) || 1
      if (item.currency === 'KRW') {
        result.costs = Object.keys(item).reduce((obj, name) => {
          if (isNotUSD(name))
            obj[name] = Math.floor(round2(item[name]) / targetRate)
          return obj
        }, {})
      } else {
        result.costs = Object.keys(item).reduce((obj, name) => {
          if (isNotUSD(name)) obj[name] = (item[name] * rate) / targetRate
          return obj
        }, {})
      }
    }
  }

  return result.costs
}

export function convertCosts(
  costs,
  displayCurrency,
  getExchangeRate,
  context,
  creditFilter
) {
  const cost = costs
    .map((c) => exchangeCost(c, displayCurrency, getExchangeRate, context))
    .reduce((p, v) => objectAdd(p, v), {})

  if (!creditFilter) return cost

  if (config.feature.creditKind) {
    cost.discount = 0
    cost.others = 0

    creditFilter.filters.forEach((filter) => {
      if (creditFilter.enabled.includes(filter.id)) {
        if (filter.isDiscount) {
          cost.discount += cost[filter.propName]
        } else {
          cost.others += cost[filter.propName]
        }
      }
    })

    cost.finalCost = cost.cost + cost.discount + cost.others
  } else {
    cost.finalCost = creditFilter.applyDiscount ? cost.final_cost : cost.cost
  }

  return cost
}

export function makeForecastData(data, month) {
  const thisMonthDays = getDaysInMonth(month)
  const ret = {
    dataPresentDays: thisMonthDays,
    forecasts: [],
  }

  if (!isCurrentMonth(month)) {
    return ret
  }

  const {
    dataPresentHours,
    dataCompleteHours,
    maxRegressionWindow,
    minRegressionWindow,
  } = config.billing.summary

  const now = new Date()
  const utcDay = now.getUTCDate()
  const utcHour = now.getUTCHours()

  if (utcDay * 24 + utcHour <= dataPresentHours) {
    ret.dataPresentDays = 1
  } else {
    ret.dataPresentDays = addHours(now, -dataPresentHours).getUTCDate()
  }

  const completeDate = addHours(now, -dataCompleteHours)
  const startDate = addDays(completeDate, -maxRegressionWindow + 1)

  const costs = []

  for (let date = startDate; date <= completeDate; date = addDays(date, 1)) {
    const dateData = data.find(
      (d) => d.date === date.toISOString().slice(0, 10)
    )
    if (dateData) {
      costs.push(dateData.cost)
    } else if (costs.length > 0) {
      costs.push(0)
    }
  }

  if (costs.length < minRegressionWindow) {
    return ret
  }

  const regressor = regression(costs)
  let offset = costs.length

  const lastDate = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    thisMonthDays,
    23,
    59,
    59
  )
  for (
    let date = addDays(completeDate, 1);
    date < lastDate;
    date = addDays(date, 1), offset += 1
  ) {
    if (
      date.getUTCFullYear() === now.getUTCFullYear() &&
      date.getUTCMonth() === now.getUTCMonth()
    ) {
      ret.forecasts.push({
        date: date.toISOString().slice(0, 10),
        cost: regressor(offset),
      })
    }
  }

  return ret
}

export function addBarToMonth(month) {
  if (month.indexOf('-') === -1) {
    return `${month.slice(0, 4)}-${month.slice(4, 6)}`
  }
  return month
}

export function getUserProjectPermissions(userResource, curAccount) {
  let userProjectPermissions = []

  if (
    userResource.allAccount ||
    userResource.projects[curAccount.id].includes('*')
  ) {
    // nothing
  } else {
    userProjectPermissions = userResource.projects[curAccount.id]
  }

  return userProjectPermissions
}

export function getQueryString(query) {
  return Object.entries(query)
    .map(([key, value]) => {
      if (Array.isArray(value)) return `${key}[]=${value.join(`&${key}[]=`)}`
      return `${key}=${value}`
    })
    .join('&')
}

export default {
  redirect,
  cloneObject: objectClone,
  objectClone,
  getDate,
  distinct,
  objectAdd,
  hasProperty,
  hasNestedProperties,
  exchangeCost,
  convertCosts,
  addBarToMonth,
  getUserProjectPermissions,
  getQueryString,
}
